// Karakterszavazó (Paleo "paleo-glyph-vote/1", 2026-10-30): a szavazó üzenet megjelenítése a témában.
// A kérdéslista az üzenet `poll.spec` mezőjében van (a Paleo írja); a szavazat a szerveren (pollVote) tárolódik, a kliens csak hívja.
// Egy kattintás = egy mentés (nincs "Beküldés" gomb); újrakattintás a választott jelöltre = visszavonás.
// Eredmény: lezárásig csak adminnak / témafelelősnek (a szerver mondja meg: canSeeResults), lezárva mindenkinek.
// A szövegek hu / en / ja; más felületi nyelven angol (mint a jelentkezési oldalon).

import { el } from "./dom.js";

const SZOVEGEK = {
  hu: {
    title: "Karakterszavazó", progress: "megválaszolva", open: "Szavazás megnyitása", loading: "A szavazás betöltése…", failed: "A szavazás nem töltődött be.", retry: "Újra",
    howto: "Kattints arra a jelöltre, amelyik a kivágáson látható jelhez hasonlít. Nem kell japánul tudnod: az alakot hasonlítsd. Újrakattintás: visszavonás. A válaszaid azonnal mentődnek.",
    mis: "? A keret mellé esik / egyik sem", unr: "* olvashatatlan", lit: "Ha tudod a helyes jelet (legfeljebb 4 karakter)", warn: "⚠ A keret valószínűleg elcsúszott.",
    candidate: "jelölt", none: "nincs karakter", saving: "Mentés…", saved: "Elmentve", saveFailed: "A mentés nem sikerült. Próbáld újra.", closedNote: "A szavazás le van zárva.",
    close: "Szavazás lezárása", reopen: "Szavazás újranyitása", resultsTitle: "Eredmény", winner: "győztes", disputed: "vitatott", noVotes: "még nincs szavazat", mislocated: "a keret mellé esik", unreadable: "olvashatatlan", voters: "szavazó",
    hiddenNote: "Az összesítés a lezárásig rejtett.", mine: "A te válaszod", question: "Kérdés", of: "/", crop: "A kivágás", placeholder: "pl. 確",
  },
  en: {
    title: "Character vote", progress: "answered", open: "Open the vote", loading: "Loading the vote…", failed: "The vote could not be loaded.", retry: "Retry",
    howto: "Click the candidate that looks like the sign in the cut-out. You do not need to know Japanese: just compare the shapes. Click again to undo. Your answers are saved at once.",
    mis: "? The frame misses it / none of them", unr: "* unreadable", lit: "If you know the right sign (up to 4 characters)", warn: "⚠ The frame is probably misplaced.",
    candidate: "candidate", none: "no character", saving: "Saving…", saved: "Saved", saveFailed: "Saving failed. Please try again.", closedNote: "The vote is closed.",
    close: "Close the vote", reopen: "Reopen the vote", resultsTitle: "Result", winner: "winner", disputed: "disputed", noVotes: "no votes yet", mislocated: "frame misses", unreadable: "unreadable", voters: "voters",
    hiddenNote: "The tally stays hidden until the vote is closed.", mine: "Your answer", question: "Question", of: "/", crop: "The cut-out", placeholder: "e.g. 確",
  },
  ja: {
    title: "文字投票", progress: "回答済み", open: "投票を開く", loading: "投票を読み込み中…", failed: "投票を読み込めませんでした。", retry: "再試行",
    howto: "切り抜きの字に似ている候補をクリックしてください。日本語が分からなくても、形を比べるだけで大丈夫です。もう一度クリックすると取り消します。回答はすぐに保存されます。",
    mis: "? 枠がずれている/どれでもない", unr: "* 読めない", lit: "正しい字が分かる場合(4文字まで)", warn: "⚠ 枠がずれている可能性があります。",
    candidate: "候補", none: "字なし", saving: "保存中…", saved: "保存しました", saveFailed: "保存できませんでした。もう一度お試しください。", closedNote: "投票は締め切られました。",
    close: "投票を締め切る", reopen: "投票を再開する", resultsTitle: "結果", winner: "決定", disputed: "意見が割れています", noVotes: "まだ票がありません", mislocated: "枠がずれている", unreadable: "読めない", voters: "票",
    hiddenNote: "集計は締め切りまで表示されません。", mine: "あなたの回答", question: "問", of: "/", crop: "切り抜き", placeholder: "例: 確",
  },
};
const tx = (uiLang, k) => (SZOVEGEK[uiLang] || SZOVEGEK.en)[k] || SZOVEGEK.en[k] || k;

const KEP_FORRAS = /^https:\/\/firebasestorage\.googleapis\.com\//;
/** Képet csak a projekt saját tárolójáról mutatunk (mint az üzenetek képei); egyébként nincs kép. */
export function biztonsagosKep(url) { return typeof url === "string" && KEP_FORRAS.test(url) ? url : null; }

/** Karakterszavazó üzenet? (a kérdéslistával, nem törölt) */
export function szavazoE(msg) {
  const p = msg && msg.poll;
  return Boolean(p && p.kind === "glyph-vote" && p.schema === "paleo-glyph-vote/1" && p.spec && Array.isArray(p.spec.questions) && p.spec.questions.length > 0 && msg.deleted !== true && msg.messageId);
}

// A megnyitott szavazók (a sor újrarajzolásakor nyitva marad); nem része a modellnek, mert csak megjelenítési állapot.
const nyitottak = new Set();

function kerdesKartya(q, uiLang, mine, closed, busy, msgId, handlers) {
  const kartya = el("div", { class: "poll-q", "data-n": String(q.n) });
  kartya.appendChild(el("div", { class: "poll-q-head", text: `${tx(uiLang, "question")} ${q.n}` }));
  if (q.mislocated === true) kartya.appendChild(el("div", { class: "poll-warn", role: "note", text: tx(uiLang, "warn") }));
  const kep = biztonsagosKep(q.crop);
  if (kep) kartya.appendChild(el("img", { class: "poll-crop", src: kep, alt: `${tx(uiLang, "crop")} ${q.n}`, loading: "lazy" }));
  const c = q.context;
  if (c && typeof c === "object") {
    kartya.appendChild(el("div", { class: "poll-context", lang: "ja" }, [
      el("span", { text: String(c.before || "") }), el("mark", { text: String(c.mark || "") }), el("span", { text: String(c.after || "") }),
    ]));
  }
  const valasz = mine && mine[String(q.n)] ? mine[String(q.n)] : null;
  const kuld = (a) => handlers.onPollVote(msgId, q.n, a);
  const csempek = el("div", { class: "poll-tiles", role: "group", "aria-label": `${tx(uiLang, "question")} ${q.n}` });
  (q.candidates || []).forEach((j, i) => {
    const kivalasztva = Boolean(valasz && valasz.choice === j.key);
    const felirat = j.blank === true ? tx(uiLang, "none") : String(j.text || "");
    const gomb = el("button", {
      type: "button", class: "poll-tile" + (kivalasztva ? " is-selected" : ""), disabled: closed || busy, "aria-pressed": kivalasztva ? "true" : "false",
      "aria-label": `${tx(uiLang, "candidate")} ${j.key}: ${felirat}`, "data-key": j.key,
      onclick: () => kuld(kivalasztva ? null : { choice: j.key }),
    });
    const csempeKep = biztonsagosKep(j.tile);
    if (csempeKep) gomb.appendChild(el("img", { class: "poll-tile-img", src: csempeKep, alt: "", loading: "lazy" }));
    gomb.appendChild(el("span", { class: "poll-tile-label", lang: "ja", text: `${i + 1}. ${j.key}: ${felirat}${kivalasztva ? " ✓" : ""}` }));
    csempek.appendChild(gomb);
  });
  kartya.appendChild(csempek);
  const gombok = el("div", { class: "poll-extra" });
  for (const [kulcs, mezo] of [["mis", "mislocated"], ["unr", "unreadable"]]) {
    const be = Boolean(valasz && valasz[mezo] === true);
    gombok.appendChild(el("button", {
      type: "button", class: "poll-extra-btn" + (be ? " is-selected" : ""), disabled: closed || busy, "aria-pressed": be ? "true" : "false",
      text: tx(uiLang, kulcs) + (be ? " ✓" : ""), onclick: () => kuld(be ? null : { [mezo]: true }),
    }));
  }
  kartya.appendChild(gombok);
  const bemenet = el("input", { type: "text", class: "poll-literal", maxlength: "8", disabled: closed || busy, placeholder: tx(uiLang, "placeholder"), "aria-label": tx(uiLang, "lit"), lang: "ja", value: valasz && valasz.literal ? valasz.literal : "" });
  const kuldLiteral = () => {
    // a szerver legfeljebb 4 KARAKTERT (kódpontot) fogad el; a maxlength UTF-16 egységet számol, ezért itt vágunk (különben elutasítaná)
    const ertek = [...bemenet.value.trim()].slice(0, 4).join("");
    if (bemenet.value.trim() !== ertek) bemenet.value = ertek;
    const regi = valasz && valasz.literal ? valasz.literal : "";
    if (ertek === regi) return;
    kuld(ertek ? { literal: ertek } : null);
  };
  bemenet.addEventListener("change", kuldLiteral);
  bemenet.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); kuldLiteral(); } });
  kartya.appendChild(el("label", { class: "poll-literal-label" }, [el("span", { class: "hint", text: tx(uiLang, "lit") }), bemenet]));
  // Billentyű: 1–5 a jelöltet választja (ha nem a szövegmezőben állunk)
  kartya.addEventListener("keydown", (e) => {
    if (e.target && e.target.tagName === "INPUT") return;
    const i = Number(e.key) - 1;
    if (!Number.isInteger(i) || i < 0 || i > 4 || e.ctrlKey || e.metaKey || e.altKey) return;
    const gomb = csempek.children[i];
    if (gomb && !gomb.disabled) { e.preventDefault(); gomb.click(); }
  });
  return kartya;
}

function eredmenySor(q, r, uiLang) {
  const sor = el("div", { class: "poll-result-row" });
  const szoveg = [];
  const sz = r && r.counts ? Object.entries(r.counts).sort((a, b) => b[1] - a[1]) : [];
  if (sz.length === 0 && !(r && (r.mislocated || r.unreadable))) szoveg.push(tx(uiLang, "noVotes"));
  else szoveg.push(sz.map(([ertek, db]) => `${ertek}: ${db}`).join(" · "));
  if (r && r.mislocated) szoveg.push(`${tx(uiLang, "mislocated")}: ${r.mislocated}`);
  if (r && r.unreadable) szoveg.push(`${tx(uiLang, "unreadable")}: ${r.unreadable}`);
  sor.appendChild(el("span", { class: "poll-result-n", text: `${q.n}.` }));
  sor.appendChild(el("span", { class: "poll-result-text", lang: "ja", text: szoveg.filter(Boolean).join(" | ") }));
  if (r && r.winner !== null && r.winner !== undefined) sor.appendChild(el("strong", { class: "poll-result-winner", text: ` ✓ ${tx(uiLang, "winner")}: ${r.winner}` }));
  else if (r && r.disputed) sor.appendChild(el("strong", { class: "poll-result-disputed", text: ` ⚠ ${tx(uiLang, "disputed")}` }));
  return sor;
}

/**
 * A szavazó doboza a hozzászólás alatt. `es.szavazok[messageId]` a betöltött állapot (main.js); ha még nincs, a kérés indul (handlers.onPollLoad).
 * @returns {HTMLElement|null}
 */
export function renderSzavazo(msg, uiLang, es, handlers) {
  if (!szavazoE(msg)) return null;
  const spec = msg.poll.spec;
  const id = msg.messageId;
  const s = es && es.szavazok ? es.szavazok[id] : null;
  const doboz = el("details", { class: "poll-box" });
  if (nyitottak.has(id)) doboz.open = true;
  doboz.addEventListener("toggle", () => {
    if (doboz.open) { nyitottak.add(id); if (!s && handlers && handlers.onPollLoad) handlers.onPollLoad(id); } else nyitottak.delete(id);
  });
  const osszes = spec.questions.length;
  const mine = s && s.fazis === "kesz" ? s.mine || {} : {};
  const megvan = Object.keys(mine).length;
  const lezarva = (s && s.fazis === "kesz" ? s.closed : msg.poll.closed) === true;
  doboz.appendChild(el("summary", { class: "poll-summary", text: `🗳 ${tx(uiLang, "title")}${spec.title ? ": " + spec.title : ""} (${megvan} ${tx(uiLang, "of")} ${osszes} ${tx(uiLang, "progress")})${lezarva ? " 🔒" : ""}` }));
  const tartalom = el("div", { class: "poll-content" });
  doboz.appendChild(tartalom);
  // A már nyitott szavazó (újrarajzolás, szobaváltás után) magától betölt; a kezelő maga gondoskodik róla, hogy egyszerre egy kérés fusson.
  if (!s && nyitottak.has(id) && handlers && handlers.onPollLoad) queueMicrotask(() => handlers.onPollLoad(id));
  if (!s || s.fazis === "toltes") {
    tartalom.appendChild(el("p", { class: "hint", text: tx(uiLang, "loading") }));
    return doboz;
  }
  if (s.fazis === "hiba") {
    tartalom.appendChild(el("p", { class: "panel-status err", text: tx(uiLang, "failed") }));
    tartalom.appendChild(el("button", { type: "button", class: "neutral-btn compact", text: tx(uiLang, "retry"), onclick: () => handlers.onPollLoad(id, true) }));
    return doboz;
  }
  tartalom.appendChild(el("p", { class: "hint poll-howto", text: tx(uiLang, "howto") }));
  if (lezarva) tartalom.appendChild(el("p", { class: "panel-status poll-closed", text: "🔒 " + tx(uiLang, "closedNote") }));
  const allapot = s.mentes === "mentes" ? tx(uiLang, "saving") : s.mentes === "hiba" ? tx(uiLang, "saveFailed") : s.mentes === "ok" ? tx(uiLang, "saved") : "";
  tartalom.appendChild(el("div", { class: "poll-save-status" + (s.mentes === "hiba" ? " err" : ""), role: "status", "aria-live": "polite", text: allapot }));
  for (const q of spec.questions) tartalom.appendChild(kerdesKartya(q, uiLang, mine, lezarva, s.mentes === "mentes", id, handlers));
  if (s.canSeeResults && s.results) {
    const e = el("div", { class: "poll-results" });
    e.appendChild(el("h4", { text: `${tx(uiLang, "resultsTitle")} (${s.voterCount || 0} ${tx(uiLang, "voters")})` }));
    for (const q of spec.questions) e.appendChild(eredmenySor(q, s.results[String(q.n)], uiLang));
    tartalom.appendChild(e);
  } else if (!lezarva) {
    tartalom.appendChild(el("p", { class: "hint", text: tx(uiLang, "hiddenNote") }));
  }
  if (s.canClose) tartalom.appendChild(el("button", { type: "button", class: "neutral-btn compact poll-close-btn", disabled: s.mentes === "mentes", text: tx(uiLang, lezarva ? "reopen" : "close"), onclick: () => handlers.onPollClose(id, !lezarva) }));
  return doboz;
}
